import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { devicesApi, slotsApi, bmPresenceApi, bmInstallsApi, BM_TOKEN_FILE_PATH } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import PageHeader from '../components/PageHeader';
import { TableScroll } from '../components/ResponsiveTable';
import usePageTitle from '../hooks/usePageTitle';
import { slotNumberOf } from '../lib/slotSelection';
import { slotState, SLOT_STATE } from '../lib/slotState';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Filter,
  Settings,
  Trash2,
  MoreVertical,
  ArrowUpDown,
  Smartphone,
  Monitor,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  X,
  Loader2,
  Laptop,
  Copy,
  Check,
  KeyRound,
  RefreshCw,
  AlertTriangle,
  Square,
  RotateCw,
  Skull,
  Cpu
} from 'lucide-react';

/*
 * Devices (Aurora, 2026-08-23). Same data, same actions as before; only the skin and the
 * breakpoints changed. Shared class strings so every input / inner row / label reads the same.
 */
const FIELD = 'bg-secondary/60 border-border placeholder:text-muted-foreground/70';
const ROW = 'rounded-xl border border-border bg-secondary/60';
const DIALOG = 'w-[calc(100vw-2rem)] sm:w-full max-h-[90vh] overflow-y-auto rounded-2xl border-border bg-card';
const SECTION_LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';

/* Status chips: one look per tone, so "online" is always success and "offline" always muted. */
const CHIP = {
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  primary: 'border-primary/30 bg-primary/10 text-primary',
  accent: 'border-accent/30 bg-accent/10 text-accent',
  muted: 'border-border bg-muted/40 text-muted-foreground',
};


// ==================== Detected Devices Section Component ====================

function DetectedDevicesSection({ onDeviceApproved, existingDevices = [] }) {
  const queryClient = useQueryClient();
  const [expandedClients, setExpandedClients] = useState({});
  // V2 dismissed via DELETE /detected-devices/{serial}; the Java stack has no such endpoint, so
  // dismiss (and the immediate post-approve hide) are client-side. A serial hidden here drops out
  // of the "NEW Detected Phones" list; a refresh of the page re-shows anything still plugged in.
  const [hiddenSerials, setHiddenSerials] = useState(() => new Set());

  /* The phone waiting on the "which deviceN?" question. A device id is not cosmetic: it is what
     every slot's deviceID points at, what the lock path resolves, and what BM maps a worker onto -
     so which one a phone takes is the customer's call, not the lowest number that happened to be
     free. Null = the dialog is closed. */
  const [pendingApproval, setPendingApproval] = useState(null);

  const hideSerial = (serial) => {
    setHiddenSerials(prev => {
      const next = new Set(prev);
      next.add(serial);
      return next;
    });
  };

  // Managed-device serials to filter out. /uiapi/devices stores the adb serial as the device udid
  // and now RETURNS it (it did not until 2026-08-27, which is why an already-added phone kept
  // offering an Add button after every reload - hiddenSerials only covered approvals made in the
  // current session, and a refresh lost them).
  const existingSerials = useMemo(() => {
    const s = new Set(existingDevices.map(d => d.udid || d.info?.udid).filter(Boolean));
    hiddenSerials.forEach(v => s.add(v));
    return s;
  }, [existingDevices, hiddenSerials]);

  // Fetch presence (which desktop clients are connected + adb devices they see). Same 5s cadence.
  const { data: detectedData, isLoading: detectedLoading, refetch } = useQuery({
    queryKey: ['bm-presence'],
    queryFn: () => bmPresenceApi.list().then(res => res.data),
    refetchInterval: 5000, // Poll every 5 seconds
  });

  /* What the account is allowed and which ids are actually free, straight from the server rather
     than counted in the browser - the limit is noOfActualDevices, and a disabled device still
     holds its id (two rows sharing one are indistinguishable to everything that looks one up). */
  const allowance = detectedData?.device_allowance;
  const freeIds = useMemo(() => allowance?.free || [], [allowance]);
  const atLimit = !!allowance && freeIds.length === 0;

  // Approve device mutation -> POST /uiapi/bm/presence/{installId}/approve { serial, label, device_id }
  const approveMutation = useMutation({
    mutationFn: ({ installId, serial, label, deviceId }) =>
      bmPresenceApi.approve(installId, serial, label, deviceId),
    onSuccess: (data, variables) => {
      hideSerial(variables.serial);
      setPendingApproval(null);
      queryClient.invalidateQueries(['bm-presence']);
      queryClient.invalidateQueries(['devices']);
      const added = data.data.device;
      toast.success(`Added as ${added?.device_id || 'a new device'}`, {
        description: added?.label ? `${added.label}${added.udid ? ` · ${added.udid}` : ''}` : undefined,
      });
      if (onDeviceApproved) onDeviceApproved(added);
    },
    onError: (error) => {
      const d = error.response?.data;
      if (d?.error === 'device_limit') {
        // Either the account is full, or the id chosen was taken between opening the picker and
        // pressing Add. Refetching re-renders the picker with what is free NOW.
        toast.error(d.message || `Device limit reached (${d.used}/${d.limit})`);
        refetch();
        return;
      }
      toast.error(d?.message || d?.detail || 'Failed to add device');
    },
  });

  // C1: Stop / Restart a captured worker on the PC. The answer is the PC's own words.
  const workerCommandMutation = useMutation({
    mutationFn: ({ installId, workerId, op }) => bmPresenceApi.workerCommand(installId, workerId, op),
    onSuccess: (res, variables) => {
      toast.success(`${variables.op} ${variables.workerId}: ${res.data?.result || 'ok'}`);
      queryClient.invalidateQueries(['bm-presence']);
    },
    onError: (error, variables) => {
      const d = error.response?.data;
      toast.error(`${variables.op} ${variables.workerId} failed: ${d?.result || d?.detail || error.message}`);
      queryClient.invalidateQueries(['bm-presence']);
    },
  });
  const sendWorkerCommand = (installId, workerId, op) => {
    if (op === 'stop' && !window.confirm(`Stop the worker "${workerId}" on this PC? The automation on that phone stops until you press Restart.`)) return;
    workerCommandMutation.mutate({ installId, workerId, op });
  };

  // Dismiss = client-side hide (no Java endpoint). Kept as a plain handler so the X is instant.
  const handleDismiss = (serial) => {
    hideSerial(serial);
    toast.success('Device dismissed');
  };

  const toggleClient = (pcId) => {
    setExpandedClients(prev => ({
      ...prev,
      [pcId]: !prev[pcId]
    }));
  };

  // Filter out devices that are already managed / approved / dismissed
  const filteredClients = useMemo(() => {
    if (!detectedData?.clients) return [];

    return detectedData.clients.map(client => ({
      ...client,
      devices: (client.devices || []).filter(device => !existingSerials.has(device.serial))
    })).filter(client => client.devices.length > 0); // Only show clients with new devices
  }, [detectedData, existingSerials]);

  // Get all connected client PCs (regardless of whether they have new devices)
  const allConnectedClients = useMemo(() => {
    if (!detectedData?.clients) return [];
    return detectedData.clients.filter(c => c.client_connected);
  }, [detectedData]);

  const totalNewDevices = filteredClients.reduce((sum, client) => sum + client.devices.length, 0);

  if (detectedLoading) {
    return (
      <Card className="aurora-card">
        <CardContent className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin text-primary" />
          <span>Checking for connected devices...</span>
        </CardContent>
      </Card>
    );
  }

  // Show nothing if no clients connected or all devices already added
  if (!detectedData?.clients?.length || filteredClients.length === 0) {
    // If there are clients but no new devices, show connected PCs info
    if (allConnectedClients.length > 0 && filteredClients.length === 0) {
      const pcNames = allConnectedClients.map(c => c.hostname || c.label).join(', ');
      const totalDevicesConnected = allConnectedClients.reduce((sum, c) => sum + (c.devices?.length || 0), 0);
      return (
        <Card className="aurora-card aurora-card-live">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-success/10 text-success ring-1 ring-success/20">
                <Smartphone className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 className="font-heading text-base font-semibold">
                  {allConnectedClients.length} Client{allConnectedClients.length !== 1 ? 's' : ''} Connected
                </h3>
                <p className="text-sm text-muted-foreground break-words">
                  Connected from: {pcNames}
                </p>
                <p className="mt-1 text-sm text-muted-foreground/70">
                  {totalDevicesConnected > 0
                    ? `All ${totalDevicesConnected} detected device${totalDevicesConnected !== 1 ? 's have' : ' has'} been added to your devices list`
                    : 'No new devices detected from connected clients'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="aurora-card aurora-card-dashed">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted/60 text-muted-foreground ring-1 ring-border">
              <Monitor className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-semibold">No Client Apps Connected</h3>
              <p className="text-sm text-muted-foreground text-pretty">
                Run the NecroMancer client app on your PC to automatically detect connected phones
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="aurora-card" data-testid="detected-devices-section">
      <CardHeader className="p-5 pb-3 sm:p-6 sm:pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
              <Smartphone className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <CardTitle className="font-heading text-base font-semibold">NEW Detected Phones</CardTitle>
              <CardDescription>
                {totalNewDevices} new device{totalNewDevices !== 1 ? 's' : ''} detected from {filteredClients.length} PC{filteredClients.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()} className="tap text-primary hover:text-primary">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-0 sm:px-6 sm:pb-6">
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <Collapsible
              key={client.installId}
              open={expandedClients[client.installId] !== false}
              onOpenChange={() => toggleClient(client.installId)}
            >
              <div className="overflow-hidden rounded-xl border border-border bg-background/40">
                <CollapsibleTrigger asChild>
                  <button className="tap flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Monitor className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate font-medium">{client.hostname || client.label}</span>
                      {client.hostname && client.label && client.hostname !== client.label && (
                        <span className="text-xs text-muted-foreground">({client.label})</span>
                      )}
                      <Badge
                        variant="outline"
                        className={cn('rounded-full', client.client_connected ? CHIP.success : CHIP.muted)}
                      >
                        {client.client_connected ? (
                          <><Wifi className="h-3 w-3 mr-1" /> Online</>
                        ) : (
                          <><WifiOff className="h-3 w-3 mr-1" /> Offline</>
                        )}
                      </Badge>
                      <Badge variant="outline" className={cn('rounded-full tnum', CHIP.primary)}>
                        {client.devices.length} device{client.devices.length !== 1 ? 's' : ''}
                      </Badge>
                      {(client.workers || []).length > 0 && (
                        <Badge variant="outline" className={cn('rounded-full tnum', CHIP.accent)}>
                          {client.workers.length} worker{client.workers.length !== 1 ? 's' : ''}
                          {client.workers.some(w => !w.alive || w.stale || w.gaveUp) && (
                            <AlertTriangle className="h-3 w-3 ml-1 text-warning" />
                          )}
                        </Badge>
                      )}
                    </div>
                    {expandedClients[client.installId] !== false ? (
                      <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-2 px-3 pb-3 sm:px-4 sm:pb-4">
                    {client.devices.map((device) => (
                      <div
                        key={device.serial}
                        className={cn(ROW, 'flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between')}
                        data-testid={`detected-device-${device.serial}`}
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Smartphone className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="truncate font-medium capitalize">
                                {device.model || device.manufacturer || 'Unknown Device'}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                              <span className="font-mono break-all">{device.serial}</span>
                              {device.android_version && (
                                <>
                                  <span>•</span>
                                  <span>Android {device.android_version}</span>
                                </>
                              )}
                              {device.screen_resolution && (
                                <>
                                  <span>•</span>
                                  <span>{device.screen_resolution}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                          <Badge
                            variant="outline"
                            className={cn(
                              'rounded-full',
                              device.state === 'device'
                                ? CHIP.success
                                : device.state === 'unauthorized'
                                ? CHIP.warning
                                : CHIP.danger
                            )}
                          >
                            {device.state === 'device' ? 'Ready' : device.state}
                          </Badge>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDismiss(device.serial)}
                            className="tap h-8 w-8 text-muted-foreground hover:bg-danger/10 hover:text-danger"
                            aria-label={`Dismiss ${device.serial}`}
                            title="Dismiss"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setPendingApproval({
                              installId: client.installId,
                              serial: device.serial,
                              model: device.model || '',
                              label: device.model || 'Device',
                              deviceId: freeIds[0] || '',
                            })}
                            disabled={approveMutation.isPending || device.state !== 'device' || atLimit}
                            className="tap"
                            title={atLimit
                              ? `Device limit reached - ${allowance.used} of ${allowance.limit} used. Remove a device to free an id.`
                              : undefined}
                            data-testid={`add-device-${device.serial}`}
                          >
                            {approveMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <PlusCircle className="h-4 w-4" />
                            )}
                            {atLimit ? 'Limit reached' : 'Add'}
                          </Button>
                        </div>
                      </div>
                    ))}
                    {/* C1: the captured workers on this PC and their supervisor state */}
                    {(client.workers || []).length > 0 && (
                      <div className="space-y-1.5 pt-2" data-testid={`workers-${client.installId}`}>
                        <div className={cn(SECTION_LABEL, 'px-1')}>Workers on this PC</div>
                        {client.workers.map((w) => (
                          <WorkerRow
                            key={w.id}
                            worker={w}
                            busy={workerCommandMutation.isPending}
                            onStop={() => sendWorkerCommand(client.installId, w.id, 'stop')}
                            onRestart={() => sendWorkerCommand(client.installId, w.id, 'restart')}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      </CardContent>

      {/* Which deviceN this phone becomes. V1 had no such question because ids were handed out by
          hand; V2 added a phone wherever it landed, which is how a fresh phone became "device17" on
          an account allowed eight. The id is load-bearing - every slot's deviceID points at it, the
          lock path resolves it, BM maps a worker onto it - so it is worth one click to choose. */}
      <Dialog open={!!pendingApproval} onOpenChange={(o) => { if (!o) setPendingApproval(null); }}>
        <DialogContent data-testid="approve-device-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add this phone</DialogTitle>
            <DialogDescription>
              {pendingApproval?.model ? `${pendingApproval.model} · ` : ''}
              <span className="font-mono">{pendingApproval?.serial}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="approve-device-id" className="text-sm">Device ID</Label>
              <Select
                value={pendingApproval?.deviceId || ''}
                onValueChange={(v) => setPendingApproval(p => ({ ...p, deviceId: v }))}
              >
                <SelectTrigger id="approve-device-id" className="mt-1.5" data-testid="approve-device-id">
                  <SelectValue placeholder="Lowest free id" />
                </SelectTrigger>
                <SelectContent>
                  {freeIds.map(id => (
                    <SelectItem key={id} value={id}>{id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {allowance
                  ? `${allowance.used} of ${allowance.limit} used · ${freeIds.length} free${
                      freeIds.length ? ` (${freeIds.slice(0, 6).join(', ')}${freeIds.length > 6 ? '…' : ''})` : ''}`
                  : 'Only ids your plan has free are offered.'}
              </p>
            </div>

            <div>
              <Label htmlFor="approve-label" className="text-sm">Label</Label>
              <Input
                id="approve-label"
                className="mt-1.5"
                value={pendingApproval?.label || ''}
                onChange={(e) => setPendingApproval(p => ({ ...p, label: e.target.value }))}
                placeholder={pendingApproval?.model || 'Device'}
                data-testid="approve-label"
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                What you will recognise it by. The model is filled in for you; you can rename it later.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingApproval(null)} className="tap">
              Cancel
            </Button>
            <Button
              onClick={() => approveMutation.mutate(pendingApproval)}
              disabled={approveMutation.isPending || !pendingApproval?.deviceId}
              className="tap"
              data-testid="approve-confirm"
            >
              {approveMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <PlusCircle className="h-4 w-4" />}
              Add as {pendingApproval?.deviceId || '…'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// C1: one captured worker (BC process) as the desktop client reports it in its presence frame.
// Faithful to BM's own local page: the same badges, the same last-exit line, Stop / Restart.
function humanMs(ms) {
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 120) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 120) return `${m} min`;
  return `${Math.round(m / 60)} h`;
}

function WorkerRow({ worker: w, busy, onStop, onRestart }) {
  const now = Date.now();
  return (
    <div
      className={cn(ROW, 'flex flex-col gap-2 p-2.5 sm:flex-row sm:items-center sm:justify-between')}
      data-testid={`worker-${w.id}`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Cpu className="h-4 w-4 shrink-0 text-accent" />
          <span className="font-mono text-sm">{w.id}</span>
          {w.serial && <span className="font-mono text-xs text-muted-foreground">{w.serial}</span>}
          <Badge variant="outline" className={cn('rounded-full', w.alive ? CHIP.success : CHIP.warning)}>
            {w.alive ? 'running' : (w.state || 'exited')}
          </Badge>
          {w.restarts > 0 && (
            <Badge variant="outline" className={cn('rounded-full tnum', CHIP.accent)}>restarted ×{w.restarts}</Badge>
          )}
          {w.stale && (
            <Badge variant="outline" className={cn('rounded-full tnum', CHIP.warning)}>
              no output {w.staleSinceMs ? humanMs(Math.max(0, now - w.staleSinceMs)) : ''}
            </Badge>
          )}
          {w.gaveUp && (
            <Badge variant="outline" className={cn('rounded-full', CHIP.danger)}><Skull className="h-3 w-3 mr-1" />gave up</Badge>
          )}
          {w.stopRequested && !w.alive && (
            <Badge variant="outline" className={cn('rounded-full', CHIP.muted)}>stopped by you</Badge>
          )}
          {w.relaunching ? (
            <Badge variant="outline" className={cn('rounded-full', CHIP.primary)}>restarting…</Badge>
          ) : (w.nextRestartAtMs > 0 && (
            <Badge variant="outline" className={cn('rounded-full tnum', CHIP.primary)}>
              restart in {humanMs(Math.max(0, w.nextRestartAtMs - now))}
            </Badge>
          ))}
        </div>
        {(w.label || w.lastExitReason) && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {w.label}{w.label && w.lastExitReason ? ' · ' : ''}{w.lastExitReason ? `last: ${w.lastExitReason}` : ''}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 sm:ml-3">
        {w.alive && (
          <Button size="sm" variant="outline" disabled={busy} onClick={onStop}
            className="tap h-7 border-danger/40 px-2 text-danger hover:bg-danger/10 hover:text-danger" data-testid={`worker-stop-${w.id}`}>
            <Square className="h-3 w-3" /> Stop
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={busy} onClick={onRestart}
          className="tap h-7 border-primary/40 px-2 text-primary hover:bg-primary/10 hover:text-primary" data-testid={`worker-restart-${w.id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
          {w.alive ? 'Restart' : 'Start'}
        </Button>
      </div>
    </div>
  );
}


// ==================== Desktop Clients Dialog ====================
//
// How a customer makes a PC appear on this page: issue an uplink token here, paste it into the
// desktop client's token file, and its WebSocket uplink registers as connected. The token is shown
// exactly ONCE (the server keeps only its hash), so it is presented with copy-to-clipboard and the
// file path where the client (BM) expects it.

function DesktopClientsDialog({ open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [issuedToken, setIssuedToken] = useState(null); // { token, id, label, wsPath, note }
  const [copied, setCopied] = useState(false);

  const { data: installs, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['bm-installs'],
    queryFn: () => bmInstallsApi.list().then(res => res.data),
    enabled: open,
    refetchInterval: open ? 10000 : false,
  });

  const createMutation = useMutation({
    mutationFn: (labelValue) => bmInstallsApi.create(labelValue),
    onSuccess: (res) => {
      setIssuedToken(res.data);
      setLabel('');
      setCopied(false);
      queryClient.invalidateQueries(['bm-installs']);
      toast.success('Desktop client token issued');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to issue token');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id) => bmInstallsApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['bm-installs']);
      queryClient.invalidateQueries(['bm-presence']);
      toast.success('Desktop client revoked');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to revoke');
    },
  });

  const copyToken = async () => {
    if (!issuedToken?.token) return;
    try {
      await navigator.clipboard.writeText(issuedToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Token copied to clipboard');
    } catch (e) {
      // Fallback for browsers/contexts without the async clipboard API.
      try {
        const ta = document.createElement('textarea');
        ta.value = issuedToken.token;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Token copied to clipboard');
      } catch (e2) {
        toast.error('Could not copy - select and copy the token manually');
      }
    }
  };

  const handleClose = (next) => {
    if (!next) {
      // Clear the one-time token from memory when the dialog closes.
      setIssuedToken(null);
      setCopied(false);
      setLabel('');
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn(DIALOG, 'sm:max-w-lg')}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Laptop className="h-5 w-5 text-primary" />
            Desktop Clients
          </DialogTitle>
          <DialogDescription className="text-pretty">
            Issue a token for each PC you run the NecroMancer desktop client on. The client's uplink
            connects with it and the PC then appears under "NEW Detected Phones".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Issue token form */}
          {!issuedToken && (
            <form
              onSubmit={(e) => { e.preventDefault(); createMutation.mutate(label); }}
              className="space-y-2"
            >
              <Label htmlFor="client_label">New desktop client label</Label>
              <div className="flex flex-col gap-2 xs:flex-row">
                <Input
                  id="client_label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Home PC"
                  className={cn(FIELD, 'min-w-0 flex-1')}
                />
                <Button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="tap shrink-0"
                >
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4" />
                  )}
                  Issue token
                </Button>
              </div>
            </form>
          )}

          {/* One-time token reveal */}
          {issuedToken && (
            <div className="space-y-3 rounded-xl border border-warning/30 bg-warning/10 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                <div className="text-sm">
                  <p className="font-medium">Copy this token now - it is shown only once.</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    The server stores only a hash. If you lose it, revoke this client and issue a new one.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <code
                  className="min-w-0 flex-1 break-all rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs text-primary"
                  data-testid="issued-token"
                >
                  {issuedToken.token}
                </code>
                <Button
                  size="icon"
                  onClick={copyToken}
                  className="tap h-9 w-9 shrink-0"
                  aria-label="Copy token"
                  title="Copy token"
                  data-testid="copy-token-btn"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>

              <div className="space-y-1 text-xs text-foreground/90">
                <p>
                  Paste it, on its own line, into the desktop client's token file:
                </p>
                <code className="block break-all rounded-lg border border-border bg-background px-3 py-1.5 font-mono">
                  {BM_TOKEN_FILE_PATH}
                </code>
                <p className="text-muted-foreground">
                  Uplink path: <code className="font-mono text-foreground/90">{issuedToken.wsPath || '/uiapi/bm/ws'}</code>.
                  The client picks up the token on its next connect (within a minute), or restart it.
                </p>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => { setIssuedToken(null); setCopied(false); }}
                className="tap"
              >
                Done
              </Button>
            </div>
          )}

          {/* Existing installs */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Your desktop clients</Label>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => refetch()}
                className="tap h-8 w-8 text-primary hover:text-primary"
                aria-label="Refresh desktop clients"
                title="Refresh"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading...
              </div>
            ) : (installs && installs.length > 0) ? (
              <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                {installs.map((inst) => (
                  <div
                    key={inst.id}
                    className={cn(ROW, 'flex items-center justify-between gap-2 p-2.5')}
                    data-testid={`install-row-${inst.id}`}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Laptop className={`h-4 w-4 shrink-0 ${inst.online ? 'text-success' : 'text-muted-foreground'}`} />
                      <div className="min-w-0">
                        <p className="truncate text-sm">{inst.label || `Client ${inst.id}`}</p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {inst.lastSeenAt ? `Last seen ${inst.lastSeenAt}` : 'Never connected'}
                          {inst.viewers > 0 ? ` • ${inst.viewers} viewer${inst.viewers !== 1 ? 's' : ''}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn('rounded-full', inst.online ? CHIP.success : CHIP.muted)}
                      >
                        {inst.online ? <><Wifi className="h-3 w-3 mr-1" /> Online</> : <><WifiOff className="h-3 w-3 mr-1" /> Offline</>}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (window.confirm(`Revoke "${inst.label || `Client ${inst.id}`}"? Its token stops working immediately.`)) {
                            revokeMutation.mutate(inst.id);
                          }
                        }}
                        disabled={revokeMutation.isPending}
                        className="tap h-8 w-8 text-muted-foreground hover:bg-danger/10 hover:text-danger"
                        aria-label={`Revoke ${inst.label || `Client ${inst.id}`}`}
                        title="Revoke"
                        data-testid={`revoke-install-${inst.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="aurora-card aurora-card-dashed py-6 text-center text-sm text-muted-foreground">
                No desktop clients yet. Issue a token above to connect your first PC.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="tap"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== Main Component ====================

export default function DevicesPageNew() {
  usePageTitle('Devices');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [desktopDialogOpen, setDesktopDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState('device_id');
  const [sortOrder, setSortOrder] = useState('asc');

  // Data fetching
  const { data: devices, isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then(res => res.data),
  });

  /*
   * Online is OBSERVED, not stored. There is no `status` column on Device - `status` was a V2
   * invention, which is why every row read "Offline" (the field was simply undefined) and why the
   * toggle moved nothing: it PUT {status} at an endpoint with no such field.
   *
   * A phone is online when a CONNECTED client is currently reporting its serial in adb state
   * "device". Anything else - unauthorized, offline, a client that has gone away - is not usable,
   * so it is not online.
   */
  const { data: livePresence } = useQuery({
    queryKey: ['bmPresence'],
    queryFn: () => bmPresenceApi.list().then(res => res.data),
    refetchInterval: 10000,
  });
  /* serial -> the PC currently reporting it. Which PC a phone is plugged into is not derivable
     from the device record at all - only presence knows, because it is a fact about right now. */
  const liveBySerial = useMemo(() => {
    const map = new Map();
    ((livePresence && livePresence.clients) || []).forEach((c) => {
      if (!c || !c.client_connected) return;
      (c.devices || []).forEach((d) => {
        if (d && d.serial && d.state === 'device') {
          map.set(d.serial, c.pc_identifier || c.hostname || 'a connected PC');
        }
      });
    });
    return map;
  }, [livePresence]);
  const isConnected = useCallback(
    (device) => !!(device && device.udid && liveBySerial.has(device.udid)),
    [liveBySerial]);
  const pcFor = useCallback(
    (device) => (device && device.udid ? liveBySerial.get(device.udid) : null) || null,
    [liveBySerial]);

  /*
   * Remember which PC last reported each phone, so unplugging one does not blank the column - "it
   * was on LAPTOP-P8H1MR42" is still the useful answer, and often the only clue to where a phone
   * physically is.
   *
   * The DURABLE answer is `last_seen_pc` on the device row, stamped server-side whenever presence
   * reports a change (BmPresenceService.recordSeen), so it is the same for every viewer and every
   * machine. localStorage stays underneath it as a fallback, for two cases that are both real: a
   * server too old to send the field, and a phone seen in THIS browser before the column existed.
   * Server first - it is the shared truth; the local copy is one browser's memory.
   */
  const LAST_PC_KEY = 'necro.devices.lastPc';
  const [lastPc, setLastPc] = useState(() => {
    try {
      const raw = window.localStorage.getItem(LAST_PC_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};                       // private window, cleared storage, corrupt JSON - all fine
    }
  });
  useEffect(() => {
    if (liveBySerial.size === 0) return;          // nothing seen: never overwrite what we remember
    setLastPc((prev) => {
      let changed = false;
      const next = { ...prev };
      liveBySerial.forEach((pc, serial) => {
        if (pc && next[serial] !== pc) { next[serial] = pc; changed = true; }
      });
      if (!changed) return prev;
      try { window.localStorage.setItem(LAST_PC_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
      return next;
    });
  }, [liveBySerial]);
  const lastPcFor = useCallback(
    (device) => (device && device.last_seen_pc)
      || (device && device.udid ? lastPc[device.udid] : null)
      || null,
    [lastPc]);

  /* The customer's device-wide pause: one switch that stops every slot on this phone. Separate
     from connection - a phone can be plugged in and switched off, or unplugged and switched on. */
  const pauseMutation = useMutation({
    mutationFn: ({ deviceId, enabled }) => devicesApi.setEnabled(deviceId, enabled),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries(['devices']);
      queryClient.invalidateQueries(['slots']);
      toast.success(vars.enabled ? `${vars.deviceId} resumed` : `${vars.deviceId} paused`, {
        description: 'Applies to every slot on this device. Takes effect between sessions; '
          + 'a session already running finishes.',
      });
    },
    onError: (error, vars) => {
      queryClient.invalidateQueries(['devices']);
      const code = error.response?.data?.error;
      toast.error(code === 'feature_disabled'
        ? 'Pausing is not enabled on this server.'
        : `Could not update ${vars.deviceId}`, { description: error.response?.data?.message });
    },
  });
  /*
   * How many slots would actually RUN on each device.
   *
   * Same query key the Slots page uses, so this is usually already cached rather than a second
   * fetch. slotState is the single resolver both pages share - it already folds in the admin
   * capacity bit, the slot's own pause AND the device pause, so a device that is switched off
   * naturally counts zero and never reaches the Ready/Running distinction below.
   */
  const { data: allSlots } = useQuery({
    queryKey: ['slots'],
    queryFn: () => slotsApi.list().then((res) => res.data),
  });
  const runnableByDevice = useMemo(() => {
    const counts = new Map();
    (allSlots || []).forEach((slot) => {
      const id = slot && slot.device_id;
      if (!id) return;
      if (slotState(slot) === SLOT_STATE.RUNNING) counts.set(id, (counts.get(id) || 0) + 1);
    });
    return counts;
  }, [allSlots]);
  const runnableCount = useCallback(
    (device) => (device && device.device_id ? runnableByDevice.get(device.device_id) : 0) || 0,
    [runnableByDevice]);

  const canPause = (device) => device && device.device_pause_enabled === true;
  const isAutomationOn = (device) => !(device && device.user_enabled === false);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => devicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
      setCreateDialogOpen(false);
      toast.success('Device created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create device');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ deviceId, data }) => devicesApi.update(deviceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
      toast.success('Device updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update device');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (deviceId) => devicesApi.delete(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
      setSelectedDevices([]);
      toast.success('Device(s) deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete device');
    },
  });

  // Filtering, sorting, and pagination
  const filteredDevices = useMemo(() => {
    if (!devices) return [];

    return devices.filter(device => {
      // Search filter
      const matchesSearch =
        device.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.device_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.device_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.info?.device_name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filter
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && isConnected(device)) ||
        (statusFilter === 'inactive' && !isConnected(device));

      return matchesSearch && matchesStatus;
    });
  }, [devices, searchQuery, statusFilter, isConnected]);

  const sortedDevices = useMemo(() => {
    if (!filteredDevices) return [];

    return [...filteredDevices].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'pc':
          // Disconnected phones have no PC at all, so they collapse to '' and group together -
          // first ascending, last descending. Sorting them among the named PCs would be noise.
          aVal = (pcFor(a) || lastPcFor(a) || '').toLowerCase();
          bVal = (pcFor(b) || lastPcFor(b) || '').toLowerCase();
          break;
        case 'device_id':
          /*
           * By NUMBER, not by string. "device10" sorts before "device2" lexically, which is the
           * order nobody wants from a device list. The API already sends device_number
           * (UiApiController computes it from the id); slotNumberOf is the fallback for anything
           * that arrives without it.
           */
          aVal = a.device_number != null ? a.device_number : (slotNumberOf(a.device_id) || 0);
          bVal = b.device_number != null ? b.device_number : (slotNumberOf(b.device_id) || 0);
          break;
        case 'label':
          aVal = a.label || '';
          bVal = b.label || '';
          break;
        case 'device_name':
          aVal = a.info?.device_name || a.device_name || '';
          bVal = b.info?.device_name || b.device_name || '';
          break;
        case 'status':
          aVal = isConnected(a) ? 1 : 0;
          bVal = isConnected(b) ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [filteredDevices, sortField, sortOrder, isConnected, pcFor, lastPcFor]);

  const paginatedDevices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedDevices.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedDevices, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedDevices.length / itemsPerPage);

  // Handlers
  const handleCreateDevice = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    createMutation.mutate({
      device_id: formData.get('device_id'),
      label: formData.get('label'),
      device_name: formData.get('device_name'),
      udid: formData.get('udid'),
      platform_version: formData.get('platform_version'),
      appium_url: formData.get('appium_url') || 'http://127.0.0.1:4723/wd/hub',
    });
  };

  /*
   * handleToggleActive / handleBulkToggleActive removed 2026-08-27. They PUT {status:'online'} at
   * /uiapi/devices/{id}, and there is no status field on Device - it was a V2 concept. The write
   * was silently a no-op and the switch never moved, which is the same shape of bug as the slots
   * page presenting a synthesised status as a customer control.
   *
   * Nothing replaces them: a phone is online because it is plugged into a running client, not
   * because someone set a flag. The chip is derived from live presence instead. To take a device
   * OUT of service, delete it (DELETE /uiapi/devices/{id}) - that is a real, guarded operation.
   */

  const handleBulkDelete = () => {
    if (window.confirm(`Delete ${selectedDevices.length} selected device(s)? This action cannot be undone.`)) {
      selectedDevices.forEach(deviceId => {
        deleteMutation.mutate(deviceId);
      });
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDevices(paginatedDevices.map(device => device.device_id));
    } else {
      setSelectedDevices([]);
    }
  };

  const handleSelectDevice = (deviceId, checked) => {
    if (checked) {
      setSelectedDevices([...selectedDevices, deviceId]);
    } else {
      setSelectedDevices(selectedDevices.filter(id => id !== deviceId));
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const emptyMessage = searchQuery || statusFilter !== 'all'
    ? 'No devices match your filters'
    : 'No devices yet. Add your first device to get started.';

  /* The per-device actions menu is shared by the table row and the phone card. */
  const deviceMenu = (device, testId) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="tap h-8 w-8" aria-label={`Actions for ${device.device_id}`} data-testid={testId}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate(`/devices/${device.device_id}/settings`)}>
          <Settings className="h-4 w-4 mr-2" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => deleteMutation.mutate(device.device_id)}
          className="text-danger focus:text-danger"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  /*
   * CONNECTED, not "online". This says one thing only: a running client on some PC is reporting
   * this phone to adb right now. "Online" invited the reading "working", which it never meant -
   * a connected phone can be switched off, and a disconnected one can be switched on.
   */
  const statusChip = (device) => {
    const on = isConnected(device);
    const pc = pcFor(device);
    return (
      <Badge
        variant="outline"
        className={cn('rounded-full', on ? CHIP.success : CHIP.muted)}
        title={on ? `Reported by ${pc} right now` : 'No connected PC is reporting this phone'}
      >
        <span className={cn('mr-1.5 h-1.5 w-1.5 rounded-full', on ? 'bg-success animate-pulse' : 'bg-muted-foreground/60')} aria-hidden />
        {on ? 'Connected' : 'Disconnected'}
      </Badge>
    );
  };

  /** Which PC a phone is plugged into. Only presence knows - it is a fact about right now. */
  const pcCell = (device) => {
    const pc = pcFor(device);
    if (pc) {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm">
          <Monitor className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{pc}
        </span>
      );
    }
    // Disconnected: show where it was last seen rather than a dash. Dimmed and prefixed so it
    // cannot be misread as "it is on that PC now".
    const last = lastPcFor(device);
    return last
      ? (
        <span
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          title={`Not connected now. It was last reported by ${last}.`}
        >
          <Monitor className="h-3.5 w-3.5 shrink-0 opacity-50" />last on {last}
        </span>
      )
      : <span className="text-xs text-muted-foreground" title="This browser has not seen this phone connected yet.">-</span>;
  };

  /** The device-wide automation switch. Off = every slot on this phone waits. */
  const automationSwitch = (device, testid) => (
    <div className="flex items-center gap-2">
      <Switch
        checked={isAutomationOn(device)}
        disabled={!canPause(device) || pauseMutation.isPending}
        onCheckedChange={() => pauseMutation.mutate({
          deviceId: device.device_id, enabled: !isAutomationOn(device),
        })}
        title={canPause(device)
          ? 'Runs or pauses EVERY slot on this device. Takes effect between sessions.'
          : 'Pausing is not enabled on this server.'}
        aria-label={`Automation for ${device.device_id}`}
        data-testid={testid}
      />
      {/*
        * THREE labels, not two. "Running" on a phone no PC is reporting would be the same lie the
        * slots page tells when a slot is on but its device is off - the switch is on, but nothing
        * can run, and the person reading it needs to know which of those two facts is missing.
        * Dimmed green for exactly that: switched on (green), nothing happening (dimmed).
        */}
      <span
        className={cn('text-xs',
          !isAutomationOn(device) ? 'text-muted-foreground'
            : (isConnected(device) && runnableCount(device) > 0) ? 'text-success' : 'text-success/60')}
        title={!isAutomationOn(device)
          ? 'Every slot on this device is paused.'
          : isConnected(device)
          ? 'Slots on this device run according to their own switches.'
          : !isConnected(device)
          ? 'Switched on, but no connected PC is reporting this phone, so nothing is running.'
          : runnableCount(device) > 0
          ? `${runnableCount(device)} slot(s) on this device are switched on and will run.`
          : 'Switched on and connected, but no slot on this device is switched on and provisioned '
            + '- so there is nothing to run yet.'}
      >
        {!isAutomationOn(device)
          ? 'Paused'
          : !isConnected(device)
          ? 'Waiting for PC'
          : runnableCount(device) > 0
          ? `Running (${runnableCount(device)})`
          : 'Ready'}
      </span>
    </div>
  );

  /* The phone card list (< md) mirrors the table; it carries its own testids so the table's stay
   * unique in the DOM (both are rendered, one is CSS-hidden by TableScroll). */
  const mobileList = paginatedDevices.length > 0 ? (
    paginatedDevices.map((device) => (
      <div key={device.device_id} className={cn(ROW, 'p-3')} data-testid={`device-card-${device.device_id}`}>
        <div className="flex items-start gap-3">
          <Checkbox
            checked={selectedDevices.includes(device.device_id)}
            onCheckedChange={(checked) => handleSelectDevice(device.device_id, checked)}
            className="mt-1"
            aria-label={`Select ${device.device_id}`}
            data-testid={`select-device-card-${device.device_id}`}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{device.label}</p>
            <button
              onClick={() => navigate(`/devices/${device.device_id}/settings`)}
              className="tap max-w-full truncate font-mono text-xs text-primary transition-colors hover:text-primary/80 hover:underline"
              data-testid={`device-id-card-link-${device.device_id}`}
            >
              {device.device_id}
            </button>
          </div>
          {deviceMenu(device, `actions-card-${device.device_id}`)}
        </div>
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 xs:grid-cols-2">
          <div className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Device name</dt>
            <dd className="truncate text-sm">{device.device_name || device.info?.device_name || 'N/A'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">Platform</dt>
            <dd className="truncate text-sm">Android {device.platform_version || device.info?.platform_version || 'N/A'}</dd>
          </div>
          <div className="min-w-0 xs:col-span-2">
            <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">UDID</dt>
            <dd className="truncate font-mono text-xs">{device.udid || device.info?.udid || 'N/A'}</dd>
          </div>
        </dl>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/60 pt-3">
          <div className="flex min-w-0 flex-col gap-1">
            {statusChip(device)}
            {(pcFor(device) || lastPcFor(device)) && (
              <span className="truncate text-[11px] text-muted-foreground">
                {pcFor(device) ? `on ${pcFor(device)}` : `last on ${lastPcFor(device)}`}
              </span>
            )}
          </div>
          {automationSwitch(device, `automation-card-${device.device_id}`)}
        </div>
      </div>
    ))
  ) : (
    <div className="aurora-card aurora-card-dashed p-6 text-center" data-testid="devices-empty-card">
      <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
        <Smartphone className="h-5 w-5" />
      </span>
      <p className="mt-3 text-sm text-muted-foreground text-pretty">{emptyMessage}</p>
      {!searchQuery && statusFilter === 'all' && (
        <Button variant="outline" size="sm" onClick={() => setCreateDialogOpen(true)} className="tap mt-4 rounded-full">
          <Plus className="h-4 w-4" /> Add Device
        </Button>
      )}
    </div>
  );

  const header = (
    <PageHeader
      icon={Smartphone}
      title="Devices Management"
      description={isLoading
        ? 'Loading devices...'
        : `${sortedDevices.length} device${sortedDevices.length !== 1 ? 's' : ''} total${selectedDevices.length > 0 ? ` • ${selectedDevices.length} selected` : ''}`}
      actions={
        <>
          <Button
            variant="outline"
            onClick={() => setDesktopDialogOpen(true)}
            className="tap border-primary/40 text-primary hover:bg-primary/10 hover:text-primary"
            data-testid="add-desktop-client-btn"
          >
            <Laptop className="h-4 w-4" />
            Add desktop client
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="tap shadow-glow" data-testid="new-device-btn">
                <Plus className="h-4 w-4" />
                Add Device
              </Button>
            </DialogTrigger>
            <DialogContent className={cn(DIALOG, 'sm:max-w-lg')}>
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Device</DialogTitle>
                <DialogDescription>
                  Configure a new Android device for automation
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateDevice}>
                <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="device_id">Device ID</Label>
                    <Input
                      id="device_id"
                      name="device_id"
                      placeholder="device1"
                      className={FIELD}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="label">Device Label</Label>
                    <Input
                      id="label"
                      name="label"
                      placeholder="My Android Device"
                      className={FIELD}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_name">Device Name</Label>
                    <Input
                      id="device_name"
                      name="device_name"
                      placeholder="Pixel 5"
                      className={FIELD}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="udid">UDID</Label>
                    <Input
                      id="udid"
                      name="udid"
                      placeholder="emulator-5554"
                      className={cn(FIELD, 'font-mono')}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="platform_version">Android Version</Label>
                    <Input
                      id="platform_version"
                      name="platform_version"
                      placeholder="11"
                      className={FIELD}
                      required
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="appium_url">Appium URL (Optional)</Label>
                    <Input
                      id="appium_url"
                      name="appium_url"
                      placeholder="http://127.0.0.1:4723/wd/hub"
                      className={cn(FIELD, 'font-mono')}
                    />
                  </div>
                </div>
                <DialogFooter className="gap-2 sm:gap-0">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="tap">
                    Cancel
                  </Button>
                  <Button type="submit" className="tap">
                    Add Device
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </>
      }
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="devices-page">
        {header}
        <div className="space-y-6" aria-busy="true" aria-label="Loading devices">
          <div className="h-16 animate-pulse rounded-xl bg-secondary/60" />
          <div className="aurora-card space-y-3 p-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-xl bg-secondary/60" />
            ))}
          </div>
          <div className="h-24 animate-pulse rounded-xl bg-secondary/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="devices-page">
      {/* Header */}
      {header}

      <DesktopClientsDialog open={desktopDialogOpen} onOpenChange={setDesktopDialogOpen} />

      {/* Filters and Bulk Actions */}
      <Card className="aurora-card">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            {/* Search */}
            <div className="min-w-0 flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by label, device ID, or device name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={cn(FIELD, 'pl-10')}
                  data-testid="search-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={(val) => {
                setStatusFilter(val);
                setCurrentPage(1);
              }}>
                <SelectTrigger className={cn(FIELD, 'tap min-w-0 flex-1 sm:w-[180px] sm:flex-none')} aria-label="Filter by status">
                  <Filter className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Devices</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="inactive">Inactive Only</SelectItem>
                </SelectContent>
              </Select>

              {/* Items Per Page */}
              <Select value={itemsPerPage.toString()} onValueChange={(val) => {
                setItemsPerPage(parseInt(val));
                setCurrentPage(1);
              }}>
                <SelectTrigger className={cn(FIELD, 'tap w-[120px]')} aria-label="Rows per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 / page</SelectItem>
                  <SelectItem value="50">50 / page</SelectItem>
                  <SelectItem value="100">100 / page</SelectItem>
                </SelectContent>
              </Select>

              {/* Bulk Actions */}
              {selectedDevices.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="tap border-primary/40 text-primary hover:bg-primary/10 hover:text-primary tnum">
                      Actions ({selectedDevices.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {/* Activate / Deactivate Selected are gone: they wrote a `status` field that
                        does not exist on Device, so they were always silent no-ops. A phone is
                        online because it is plugged into a running client - there is nothing to
                        activate. Removing a device is Delete, below, which is real and guarded. */}
                    <DropdownMenuItem onClick={handleBulkDelete} className="text-danger focus:text-danger">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="aurora-card overflow-hidden">
        <CardContent className="p-3 md:p-0">
          <TableScroll mobile={mobileList}>
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-secondary/50">
                  <TableHead className="w-12 pl-4">
                    <Checkbox
                      checked={selectedDevices.length === paginatedDevices.length && paginatedDevices.length > 0}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all devices on this page"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="w-32 cursor-pointer select-none" onClick={() => toggleSort('device_id')}>
                    <div className="flex items-center gap-2">
                      Device ID
                      <ArrowUpDown className={cn('h-4 w-4', sortField === 'device_id' && 'text-primary')} />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('label')}>
                    <div className="flex items-center gap-2">
                      Label
                      <ArrowUpDown className={cn('h-4 w-4', sortField === 'label' && 'text-primary')} />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('device_name')}>
                    <div className="flex items-center gap-2">
                      Device Name
                      <ArrowUpDown className={cn('h-4 w-4', sortField === 'device_name' && 'text-primary')} />
                    </div>
                  </TableHead>
                  <TableHead>UDID</TableHead>
                  <TableHead>Platform</TableHead>
                  {/* Two different facts, so two columns. Connection is observed and read-only;
                      Automation is the customer's own switch. Conflating them was the old
                      "Status" column, which showed neither. */}
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>
                    <div className="flex items-center gap-2">
                      Connection
                      <ArrowUpDown className={cn('h-4 w-4', sortField === 'status' && 'text-primary')} />
                    </div>
                  </TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('pc')}>
                    <div className="flex items-center gap-2">
                      On PC
                      <ArrowUpDown className={cn('h-4 w-4', sortField === 'pc' && 'text-primary')} />
                    </div>
                  </TableHead>
                  <TableHead>Automation</TableHead>
                  <TableHead className="pr-4 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedDevices.length > 0 ? (
                  paginatedDevices.map((device) => (
                    <TableRow
                      key={device.device_id}
                      className="border-border hover:bg-secondary/50"
                      data-testid={`device-row-${device.device_id}`}
                    >
                      <TableCell className="pl-4">
                        <Checkbox
                          checked={selectedDevices.includes(device.device_id)}
                          onCheckedChange={(checked) => handleSelectDevice(device.device_id, checked)}
                          aria-label={`Select ${device.device_id}`}
                          data-testid={`select-device-${device.device_id}`}
                        />
                      </TableCell>
                      <TableCell className="min-w-0">
                        <button
                          onClick={() => navigate(`/devices/${device.device_id}/settings`)}
                          className="max-w-full truncate font-mono text-sm text-primary transition-colors hover:text-primary/80 hover:underline"
                          data-testid={`device-id-link-${device.device_id}`}
                        >
                          {device.device_id}
                        </button>
                      </TableCell>
                      <TableCell className="min-w-0">
                        <p className="truncate font-medium">{device.label}</p>
                      </TableCell>
                      <TableCell className="min-w-0">
                        <span className="block truncate text-sm">{device.device_name || device.info?.device_name || 'N/A'}</span>
                      </TableCell>
                      <TableCell className="min-w-0">
                        <span className="block truncate font-mono text-xs text-muted-foreground">{device.udid || device.info?.udid || 'N/A'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="whitespace-nowrap text-sm">Android {device.platform_version || device.info?.platform_version || 'N/A'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {/* Read-only: online is observed from live presence, not a stored flag
                              anyone can set. See the note where the toggles used to be. */}
                          {statusChip(device)}
                        </div>
                      </TableCell>
                      <TableCell>{pcCell(device)}</TableCell>
                      <TableCell>{automationSwitch(device, `automation-${device.device_id}`)}</TableCell>
                      <TableCell className="pr-4 text-right">
                        {deviceMenu(device, `actions-${device.device_id}`)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-muted-foreground">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableScroll>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-3 flex flex-col gap-3 border-t border-border px-1 pt-3 sm:flex-row sm:items-center sm:justify-between md:mt-0 md:p-4">
              <div className="text-sm text-muted-foreground tnum">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedDevices.length)} of {sortedDevices.length} devices
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="tap"
                  data-testid="prev-page-btn"
                >
                  Previous
                </Button>
                <div className="flex flex-wrap items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="tap tnum"
                        aria-current={currentPage === pageNum ? 'page' : undefined}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="tap"
                  data-testid="next-page-btn"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detected Devices Section - At Bottom */}
      <DetectedDevicesSection
        onDeviceApproved={() => queryClient.invalidateQueries(['devices'])}
        existingDevices={devices || []}
      />
    </div>
  );
}
